import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Calendar, Search, FileText, Image, Video, Mail, Sparkles, TrendingUp, Clock, Facebook, Instagram, Link2, CheckCircle, Send, X } from 'lucide-react';
import { marketingAPI } from '../services/api';
import { format } from 'date-fns';

export default function MarketingPage() {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('materials'); // 'materials' or 'calendar'

  const { data: materials, isLoading } = useQuery({
    queryKey: ['marketing-materials', activeTab, searchQuery],
    queryFn: () => marketingAPI.getMaterials({
      category: activeTab !== 'all' ? activeTab : undefined,
      search: searchQuery
    }),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity
  });

  const { data: calendar } = useQuery({
    queryKey: ['marketing-calendar'],
    queryFn: () => marketingAPI.getCalendar(),
    enabled: viewMode === 'calendar',
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity
  });

  const { data: categories } = useQuery({
    queryKey: ['marketing-categories'],
    queryFn: () => marketingAPI.getCategories(),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity
  });

  const tabs = [
    { id: 'all', label: 'All Materials', icon: FileText },
    { id: 'social_media', label: 'Social Media', icon: Image },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'video', label: 'Video', icon: Video },
  ];

  const materialsCount = materials?.data?.materials?.length || 0;
  const currentDay = calendar?.data?.currentDay || 0;
  const [facebookConnected, setFacebookConnected] = useState(false);
  const [instagramConnected, setInstagramConnected] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [postCaption, setPostCaption] = useState('');
  const [postToFacebook, setPostToFacebook] = useState(true);
  const [postToInstagram, setPostToInstagram] = useState(true);
  const [isPosting, setIsPosting] = useState(false);

  // Get relevant stock photo based on material category - all fitness/yoga themed
  const getStockPhotoUrl = (material) => {
    const category = material.category?.toLowerCase() || 'general';
    
    // Fitness/Yoga themed stock photos - using reliable Unsplash URLs
    // All images are yoga/fitness related for Instagram posts, email templates, and posters
    const photoMap = {
      'social_media': 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=800&h=600', // Yoga pose - Instagram
      'email': 'https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&w=800&h=600', // Yoga meditation - Email
      'video': 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=800&h=600', // Yoga pose - Video
      'general': 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=800&h=600' // Yoga fitness poster - Poster
    };
    
    return photoMap[category] || photoMap['general'];
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Social Media Connections */}
      <div className="card border-2 border-[var(--border-subtle)]" style={{ background: 'var(--bg-surface)' }}>
        <div>
          <h2 className="text-lg md:text-xl font-bold text-[var(--text-primary)] mb-1">Social Media Connections</h2>
          <p className="text-[var(--text-secondary)] text-xs md:text-sm">Connect your accounts to post directly to Facebook and Instagram</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {/* Facebook Connection */}
          <div
            className={`p-5 rounded-xl border-2 transition-all ${
              facebookConnected
                ? 'border-blue-300'
                : 'border-[var(--border-subtle)] hover:border-[var(--primary)]'
            }`}
            style={{ background: facebookConnected ? 'rgba(59, 130, 246, 0.1)' : 'var(--bg-elevated)' }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  facebookConnected ? 'bg-blue-600' : 'bg-blue-100'
                }`}>
                  <Facebook className={`w-6 h-6 ${facebookConnected ? 'text-white' : 'text-blue-600'}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-[var(--text-primary)]">Facebook</h3>
                  <p className="text-xs text-[var(--text-secondary)]">
                    {facebookConnected ? 'Connected' : 'Not connected'}
                  </p>
                </div>
              </div>
              {facebookConnected && (
                <CheckCircle className="w-6 h-6 text-green-600" />
              )}
            </div>
            {facebookConnected ? (
              <div className="space-y-2">
                <p className="text-sm text-[var(--text-secondary)]">Your Facebook account is connected</p>
                <button
                  onClick={() => setFacebookConnected(false)}
                  className="btn btn-outline w-full text-sm"
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  // TODO: Implement Facebook OAuth connection
                  setFacebookConnected(true);
                }}
                className="btn w-full text-sm text-white font-semibold flex items-center justify-center"
                style={{ backgroundColor: '#1877F2' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#166FE5'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#1877F2'}
              >
                <Link2 className="w-4 h-4 mr-2" />
                Connect Facebook
              </button>
            )}
          </div>

          {/* Instagram Connection */}
          <div
            className={`p-5 rounded-xl border-2 transition-all ${
              instagramConnected
                ? 'border-purple-300'
                : 'border-[var(--border-subtle)] hover:border-[var(--primary)]'
            }`}
            style={{ background: instagramConnected ? 'rgba(168, 85, 247, 0.1)' : 'var(--bg-elevated)' }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  instagramConnected 
                    ? 'bg-gradient-to-br from-purple-600 to-pink-600' 
                    : 'bg-gradient-to-br from-purple-100 to-pink-100'
                }`}>
                  <Instagram className={`w-6 h-6 ${instagramConnected ? 'text-white' : 'text-purple-600'}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-[var(--text-primary)]">Instagram</h3>
                  <p className="text-xs text-[var(--text-secondary)]">
                    {instagramConnected ? 'Connected' : 'Not connected'}
                  </p>
                </div>
              </div>
              {instagramConnected && (
                <CheckCircle className="w-6 h-6 text-green-600" />
              )}
            </div>
            {instagramConnected ? (
              <div className="space-y-2">
                <p className="text-sm text-[var(--text-secondary)]">Your Instagram account is connected</p>
                <button
                  onClick={() => setInstagramConnected(false)}
                  className="btn btn-outline w-full text-sm"
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  // TODO: Implement Instagram OAuth connection
                  setInstagramConnected(true);
                }}
                className="w-full text-sm text-white font-semibold flex items-center justify-center transition-all duration-300 rounded-lg px-4 py-2.5"
                style={{
                  background: 'linear-gradient(to right, #9333ea, #db2777)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(to right, #7e22ce, #be185d)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(to right, #9333ea, #db2777)';
                }}
              >
                <Link2 className="w-4 h-4 mr-2" />
                Connect Instagram
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div>
            <h1 className="text-3xl font-bold text-[var(--text-primary)]">Marketing Materials</h1>
            <p className="text-[var(--text-secondary)] text-sm">Access your 60-day marketing campaign</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('materials')}
            className={`btn flex items-center gap-2 ${
              viewMode === 'materials' 
                ? 'btn-primary shadow-md' 
                : 'btn-outline hover:bg-[var(--highlight-5)]'
            }`}
          >
            <FileText className="w-5 h-5" />
            Materials
            {materialsCount > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                {materialsCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setViewMode('calendar')}
            className={`btn flex items-center gap-2 ${
              viewMode === 'calendar' 
                ? 'btn-primary shadow-md' 
                : 'btn-outline hover:bg-[var(--highlight-5)]'
            }`}
          >
            <Calendar className="w-5 h-5" />
            Calendar
            {currentDay > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                Day {currentDay}
              </span>
            )}
          </button>
        </div>
      </div>

      {viewMode === 'materials' ? (
        <>
          {/* Search & Tabs */}
          <div className="card border-2 border-[var(--border-subtle)]" style={{ background: 'var(--bg-surface)' }}>
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              {/* Tabs */}
              <div className="flex gap-2 flex-wrap w-full md:w-auto">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const count = materials?.data?.materials?.filter(m => 
                    activeTab === 'all' || m.category === tab.id
                  ).length || 0;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg whitespace-nowrap transition-all font-semibold ${
                        activeTab === tab.id
                          ? 'bg-primary-600 text-white shadow-md transform scale-105 border-2 border-primary-700'
                          : 'text-[var(--text-secondary)] hover:bg-[var(--highlight-5)] border-2 border-[var(--border-subtle)]'
                      }`}
                      style={activeTab !== tab.id ? { background: 'var(--bg-elevated)' } : {}}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{tab.label}</span>
                      {count > 0 && (
                        <span
                          className={`ml-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                            activeTab === tab.id
                              ? 'bg-white/30 text-white border border-white/40'
                              : 'bg-[var(--highlight-5)] text-[var(--text-secondary)] border border-[var(--border-subtle)]'
                          }`}
                        >
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Search */}
              <div className="relative w-full md:w-72">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--text-muted)] w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search materials..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input pl-10 border-[var(--border-subtle)] focus:border-primary-500 focus:ring-primary-500"
                  style={{ background: 'var(--bg-elevated)' }}
                />
              </div>
            </div>
          </div>

          {/* Materials Grid */}
          {isLoading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-primary-600 mx-auto mb-4"></div>
              <p className="text-[var(--text-secondary)]">Loading materials...</p>
            </div>
          ) : materials?.data?.materials?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {materials.data.materials.map((material) => (
                <div 
                  key={material.id} 
                  className="card group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-transparent hover:border-[var(--border-subtle)] overflow-hidden"
                >
                  {/* Preview */}
                  <div className="aspect-video bg-gradient-to-br from-primary-100 to-accent-100 rounded-lg overflow-hidden mb-4 relative">
                    <img
                      src={material.thumbnailUrl || getStockPhotoUrl(material)}
                      alt={material.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        // Fallback to stock photo if thumbnail fails to load
                        if (e.target.src !== getStockPhotoUrl(material)) {
                          e.target.src = getStockPhotoUrl(material);
                        } else {
                          // Final fallback to placeholder
                          e.target.style.display = 'none';
                          if (e.target.nextElementSibling) {
                            e.target.nextElementSibling.style.display = 'flex';
                          }
                        }
                      }}
                    />
                    <div className="w-full h-full flex items-center justify-center absolute inset-0 bg-gradient-to-br from-primary-100 to-accent-100" style={{ display: 'none' }}>
                      <FileText className="w-16 h-16 text-primary-300" />
                    </div>
                    {material.campaignDay && (
                      <div className="absolute top-2 right-2 text-xs font-semibold px-2.5 py-1 rounded-full shadow-lg z-10" style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)' }}>
                        Day {material.campaignDay}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="space-y-3">
                    <div>
                      <h3 className="font-semibold text-[var(--text-primary)] line-clamp-2 mb-1 group-hover:text-[var(--text-secondary)] transition-colors">
                        {material.title}
                      </h3>
                      {material.caption && (
                        <p className="text-xs text-[var(--text-secondary)] line-clamp-2">{material.caption}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs bg-primary-100 text-[var(--text-secondary)] px-2.5 py-1 rounded-full capitalize font-medium border border-[var(--border-subtle)]">
                        {material.category.replace('_', ' ')}
                      </span>
                      <span className="text-xs bg-secondary-100 text-[var(--text-secondary)] px-2.5 py-1 rounded-full uppercase font-medium border border-[var(--border-subtle)]">
                        {material.fileType}
                      </span>
                    </div>

                    <div className="flex gap-2">
                      {activeTab === 'social_media' && (facebookConnected || instagramConnected) && (
                        <button 
                          onClick={() => {
                            setSelectedMaterial(material);
                            setPostCaption(material.caption || '');
                          }}
                          className="btn btn-primary flex-1 text-sm mt-2 group-hover:bg-primary-700 transition-colors shadow-md hover:shadow-lg"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Post
                        </button>
                      )}
                      <button className="btn btn-outline text-sm mt-2">
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card border-2 border-[var(--border-subtle)] text-center py-16" style={{ background: 'var(--bg-surface)' }}>
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--highlight-10)' }}>
                <FileText className="w-10 h-10 text-[var(--text-secondary)]" />
              </div>
              <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-2">No materials found</h3>
              <p className="text-[var(--text-secondary)] mb-6">
                {searchQuery 
                  ? `No materials match "${searchQuery}"` 
                  : activeTab !== 'all' 
                    ? `No ${tabs.find(t => t.id === activeTab)?.label.toLowerCase()} materials available`
                    : 'Start your marketing campaign to access materials'}
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="btn btn-outline"
                >
                  Clear search
                </button>
              )}
            </div>
          )}

          {/* Social Media Posting Section - Only show in Social Media tab */}
          {activeTab === 'social_media' && (facebookConnected || instagramConnected) && (
            <div className="space-y-6 mt-8">
              {/* Post to Feed Section */}
              <div className="card border-2 border-[var(--border-subtle)]" style={{ background: 'var(--bg-surface)' }}>
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-2">Post to Feed</h2>
                  <p className="text-[var(--text-secondary)]">Share your marketing materials as regular posts on your social media feeds</p>
                </div>

                {selectedMaterial ? (
                  <div className="space-y-6">
                    {/* Selected Material Preview */}
                    <div className="rounded-xl p-6 border-2 border-[var(--border-subtle)]" style={{ background: 'var(--bg-elevated)' }}>
                      <div className="flex items-start justify-between mb-4">
                        <h3 className="text-lg font-semibold text-[var(--text-primary)]">Selected Material</h3>
                        <button
                          onClick={() => {
                            setSelectedMaterial(null);
                            setPostCaption('');
                          }}
                          className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="flex gap-4">
                        <div className="w-32 h-32 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-primary-100 to-accent-100">
                          <img
                            src={selectedMaterial.thumbnailUrl || getStockPhotoUrl(selectedMaterial)}
                            alt={selectedMaterial.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // Fallback to stock photo if thumbnail fails to load
                              if (e.target.src !== getStockPhotoUrl(selectedMaterial)) {
                                e.target.src = getStockPhotoUrl(selectedMaterial);
                              } else {
                                // Final fallback - show placeholder
                                e.target.style.display = 'none';
                              }
                            }}
                          />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-[var(--text-primary)] mb-2">{selectedMaterial.title}</h4>
                          {selectedMaterial.caption && (
                            <p className="text-sm text-[var(--text-secondary)] mb-2">{selectedMaterial.caption}</p>
                          )}
                          <div className="flex gap-2">
                            <span className="text-xs bg-primary-100 text-[var(--text-secondary)] px-2 py-1 rounded-full capitalize">
                              {selectedMaterial.category.replace('_', ' ')}
                            </span>
                            <span className="text-xs bg-secondary-100 text-[var(--text-secondary)] px-2 py-1 rounded-full uppercase">
                              {selectedMaterial.fileType}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Post Options */}
                    <div className="rounded-xl p-6 border-2 border-[var(--border-subtle)]" style={{ background: 'var(--bg-elevated)' }}>
                      <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Post to Platforms</h3>
                      
                      <div className="space-y-4">
                        {/* Platform Selection */}
                        <div className="flex gap-4">
                          {facebookConnected && (
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={postToFacebook}
                                onChange={(e) => setPostToFacebook(e.target.checked)}
                                className="w-5 h-5 text-[var(--text-secondary)] rounded"
                              />
                              <div className="flex items-center gap-2">
                                <Facebook className="w-5 h-5 text-blue-600" />
                                <span className="font-medium text-[var(--text-primary)]">Facebook</span>
                              </div>
                            </label>
                          )}
                          {instagramConnected && (
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={postToInstagram}
                                onChange={(e) => setPostToInstagram(e.target.checked)}
                                className="w-5 h-5 text-[var(--text-secondary)] rounded"
                              />
                              <div className="flex items-center gap-2">
                                <Instagram className="w-5 h-5 text-purple-600" />
                                <span className="font-medium text-[var(--text-primary)]">Instagram</span>
                              </div>
                            </label>
                          )}
                        </div>

                        {/* Caption Input */}
                        <div>
                          <label className="label">Post Caption</label>
                          <textarea
                            value={postCaption}
                            onChange={(e) => setPostCaption(e.target.value)}
                            placeholder="Write a caption for your post..."
                            className="input min-h-[100px] resize-y"
                            rows={4}
                          />
                          <p className="text-xs text-[var(--text-muted)] mt-1">
                            {postCaption.length} characters
                          </p>
                        </div>

                        {/* Post Button */}
                        <button
                          onClick={async () => {
                            if (!postToFacebook && !postToInstagram) {
                              alert('Please select at least one platform to post to');
                              return;
                            }
                            setIsPosting(true);
                            // TODO: Implement actual posting to Facebook/Instagram APIs
                            setTimeout(() => {
                              setIsPosting(false);
                              alert(`Posted to ${postToFacebook ? 'Facebook' : ''}${postToFacebook && postToInstagram ? ' and ' : ''}${postToInstagram ? 'Instagram' : ''}!`);
                              setSelectedMaterial(null);
                              setPostCaption('');
                            }, 2000);
                          }}
                          disabled={isPosting || (!postToFacebook && !postToInstagram)}
                          className="btn btn-primary w-full text-lg py-3 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isPosting ? (
                            <>
                              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                              Posting...
                            </>
                          ) : (
                            <>
                              <Send className="w-5 h-5 mr-2" />
                              Post to {postToFacebook && postToInstagram ? 'Facebook & Instagram' : postToFacebook ? 'Facebook' : 'Instagram'}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--highlight-10)' }}>
                      <Send className="w-10 h-10 text-[var(--text-secondary)]" />
                    </div>
                    <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-2">Select a Material to Post</h3>
                    <p className="text-[var(--text-secondary)] mb-6">
                      Click on a material card above and use the "Post" button to select it for posting
                    </p>
                    <div className="flex items-center justify-center gap-4 text-sm text-[var(--text-secondary)]">
                      {facebookConnected && (
                        <div className="flex items-center gap-2">
                          <Facebook className="w-5 h-5 text-blue-600" />
                          <span>Facebook Ready</span>
                        </div>
                      )}
                      {instagramConnected && (
                        <div className="flex items-center gap-2">
                          <Instagram className="w-5 h-5 text-purple-600" />
                          <span>Instagram Ready</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Post Advertisement Section */}
              <div className="card border-2 border-[var(--border-subtle)]" style={{ background: 'var(--bg-surface)' }}>
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-2">Post Advertisement</h2>
                  <p className="text-[var(--text-secondary)]">Create and publish paid advertisements on Facebook and Instagram</p>
                </div>

                {selectedMaterial ? (
                  <div className="space-y-6">
                    {/* Selected Material Preview */}
                    <div className="rounded-xl p-6 border-2 border-[var(--border-subtle)]" style={{ background: 'var(--bg-elevated)' }}>
                      <div className="flex items-start justify-between mb-4">
                        <h3 className="text-lg font-semibold text-[var(--text-primary)]">Selected Material</h3>
                        <button
                          onClick={() => {
                            setSelectedMaterial(null);
                            setPostCaption('');
                          }}
                          className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="flex gap-4">
                        <div className="w-32 h-32 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-primary-100 to-accent-100">
                          <img
                            src={selectedMaterial.thumbnailUrl || getStockPhotoUrl(selectedMaterial)}
                            alt={selectedMaterial.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // Fallback to stock photo if thumbnail fails to load
                              if (e.target.src !== getStockPhotoUrl(selectedMaterial)) {
                                e.target.src = getStockPhotoUrl(selectedMaterial);
                              } else {
                                // Final fallback - show placeholder
                                e.target.style.display = 'none';
                              }
                            }}
                          />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-[var(--text-primary)] mb-2">{selectedMaterial.title}</h4>
                          {selectedMaterial.caption && (
                            <p className="text-sm text-[var(--text-secondary)] mb-2">{selectedMaterial.caption}</p>
                          )}
                          <div className="flex gap-2">
                            <span className="text-xs bg-primary-100 text-[var(--text-secondary)] px-2 py-1 rounded-full capitalize">
                              {selectedMaterial.category.replace('_', ' ')}
                            </span>
                            <span className="text-xs bg-secondary-100 text-[var(--text-secondary)] px-2 py-1 rounded-full uppercase">
                              {selectedMaterial.fileType}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Advertisement Options */}
                    <div className="rounded-xl p-6 border-2 border-[var(--border-subtle)]" style={{ background: 'var(--bg-elevated)' }}>
                      <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Create Advertisement</h3>
                      
                      <div className="space-y-4">
                        {/* Platform Selection */}
                        <div className="flex gap-4">
                          {facebookConnected && (
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={postToFacebook}
                                onChange={(e) => setPostToFacebook(e.target.checked)}
                                className="w-5 h-5 text-[var(--text-secondary)] rounded"
                              />
                              <div className="flex items-center gap-2">
                                <Facebook className="w-5 h-5 text-blue-600" />
                                <span className="font-medium text-[var(--text-primary)]">Facebook Ads</span>
                              </div>
                            </label>
                          )}
                          {instagramConnected && (
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={postToInstagram}
                                onChange={(e) => setPostToInstagram(e.target.checked)}
                                className="w-5 h-5 text-[var(--text-secondary)] rounded"
                              />
                              <div className="flex items-center gap-2">
                                <Instagram className="w-5 h-5 text-purple-600" />
                                <span className="font-medium text-[var(--text-primary)]">Instagram Ads</span>
                              </div>
                            </label>
                          )}
                        </div>

                        {/* Ad Copy */}
                        <div>
                          <label className="label">Advertisement Copy</label>
                          <textarea
                            value={postCaption}
                            onChange={(e) => setPostCaption(e.target.value)}
                            placeholder="Write your advertisement copy..."
                            className="input min-h-[100px] resize-y"
                            rows={4}
                          />
                          <p className="text-xs text-[var(--text-muted)] mt-1">
                            {postCaption.length} characters
                          </p>
                        </div>

                        {/* Budget Input (for ads) */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="label">Daily Budget ($)</label>
                            <input
                              type="number"
                              placeholder="10.00"
                              className="input"
                              min="1"
                              step="0.01"
                            />
                          </div>
                          <div>
                            <label className="label">Duration (days)</label>
                            <input
                              type="number"
                              placeholder="7"
                              className="input"
                              min="1"
                            />
                          </div>
                        </div>

                        {/* Create Ad Button */}
                        <button
                          onClick={async () => {
                            if (!postToFacebook && !postToInstagram) {
                              alert('Please select at least one platform for your advertisement');
                              return;
                            }
                            setIsPosting(true);
                            // TODO: Implement actual advertisement creation via Facebook/Instagram Ads API
                            setTimeout(() => {
                              setIsPosting(false);
                              alert(`Advertisement created on ${postToFacebook ? 'Facebook' : ''}${postToFacebook && postToInstagram ? ' and ' : ''}${postToInstagram ? 'Instagram' : ''}!`);
                              setSelectedMaterial(null);
                              setPostCaption('');
                            }, 2000);
                          }}
                          disabled={isPosting || (!postToFacebook && !postToInstagram)}
                          className="btn btn-primary w-full text-lg py-3 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                        >
                          {isPosting ? (
                            <>
                              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                              Creating Advertisement...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-5 h-5 mr-2" />
                              Create Advertisement on {postToFacebook && postToInstagram ? 'Facebook & Instagram' : postToFacebook ? 'Facebook' : 'Instagram'}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--highlight-10)' }}>
                      <Sparkles className="w-10 h-10 text-[var(--text-secondary)]" />
                    </div>
                    <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-2">Select a Material for Advertisement</h3>
                    <p className="text-[var(--text-secondary)] mb-6">
                      Click on a material card above and use the "Post" button to select it for creating an advertisement
                    </p>
                    <div className="flex items-center justify-center gap-4 text-sm text-[var(--text-secondary)]">
                      {facebookConnected && (
                        <div className="flex items-center gap-2">
                          <Facebook className="w-5 h-5 text-blue-600" />
                          <span>Facebook Ads Ready</span>
                        </div>
                      )}
                      {instagramConnected && (
                        <div className="flex items-center gap-2">
                          <Instagram className="w-5 h-5 text-purple-600" />
                          <span>Instagram Ads Ready</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        /* Calendar View */
        <div className="space-y-6">
          {/* Calendar Header Stats */}
          {calendar?.data?.calendar && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="card border-2 border-[var(--border-subtle)]" style={{ background: 'var(--bg-surface)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[var(--text-secondary)] mb-1">Current Day</p>
                    <p className="text-3xl font-bold text-[var(--text-primary)]">{currentDay}</p>
                    <p className="text-xs text-[var(--text-secondary)] mt-1">of 60 days</p>
                  </div>
                  <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
              <div className="card border-2 border-[var(--border-subtle)]" style={{ background: 'var(--bg-surface)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[var(--text-secondary)] mb-1">Total Materials</p>
                    <p className="text-3xl font-bold text-[var(--text-primary)]">
                      {Object.values(calendar.data.calendar).reduce((sum, day) => sum + day.materials.length, 0)}
                    </p>
                    <p className="text-xs text-[var(--text-secondary)] mt-1">across all days</p>
                  </div>
                  <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
              <div className="card border-2 border-[var(--border-subtle)]" style={{ background: 'var(--bg-surface)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[var(--text-secondary)] mb-1">Progress</p>
                    <p className="text-3xl font-bold text-[var(--text-primary)]">
                      {Math.round((currentDay / 60) * 100)}%
                    </p>
                    <p className="text-xs text-[var(--text-secondary)] mt-1">campaign complete</p>
                  </div>
                  <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-[var(--text-primary)]">60-Day Marketing Calendar</h2>
                <p className="text-[var(--text-secondary)] mt-1">Track your campaign progress day by day</p>
              </div>
            </div>
            
            {calendar?.data?.calendar ? (
              <div className="space-y-3">
                {Object.values(calendar.data.calendar).map((day) => (
                  <div
                    key={day.day}
                    className={`p-5 rounded-xl border-2 transition-all ${
                      day.day === calendar.data.currentDay
                        ? 'border-primary-500 shadow-lg'
                        : 'border-[var(--border-subtle)] hover:border-[var(--border-subtle)] hover:shadow-md'
                    }`}
                    style={{ background: day.day === calendar.data.currentDay ? 'var(--primary-10)' : 'var(--bg-surface)' }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg ${
                            day.day === calendar.data.currentDay
                              ? 'bg-primary-600 text-white'
                              : 'text-[var(--text-secondary)]'
                          }`}
                          style={day.day !== calendar.data.currentDay ? { background: 'var(--highlight-10)' } : {}}
                        >
                          {day.day}
                        </div>
                        <div>
                          <h3 className="font-semibold text-[var(--text-primary)] flex items-center gap-2">
                            Day {day.day}
                            {day.day === calendar.data.currentDay && (
                              <span className="text-xs bg-primary-600 text-white px-2.5 py-1 rounded-full font-semibold">
                                Today
                              </span>
                            )}
                          </h3>
                          <p className="text-sm text-[var(--text-secondary)] flex items-center gap-1 mt-1">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(day.date), 'EEEE, MMMM d, yyyy')}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span
                          className={`text-sm font-semibold px-3 py-1 rounded-full ${
                            day.materials.length > 0
                              ? 'text-green-600'
                              : 'text-[var(--text-secondary)]'
                          }`}
                          style={{ background: 'var(--highlight-5)', border: '1px solid var(--border-subtle)' }}
                        >
                          {day.materials.length} {day.materials.length === 1 ? 'material' : 'materials'}
                        </span>
                      </div>
                    </div>

                    {day.materials.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        {day.materials.map((material) => (
                          <div
                            key={material.id}
                            className="p-3 rounded-lg border border-[var(--border-subtle)] hover:border-[var(--primary)] hover:shadow-md transition-all group"
                            style={{ background: 'var(--bg-elevated)' }}
                          >
                            <p className="font-medium text-[var(--text-primary)] truncate mb-1 group-hover:text-[var(--text-secondary)]">
                              {material.title}
                            </p>
                            <div className="flex items-center gap-2">
                              <span className="text-xs bg-[var(--highlight-5)] text-[var(--text-secondary)] px-2 py-0.5 rounded-full capitalize border border-[var(--border-subtle)]">
                                {material.category.replace('_', ' ')}
                              </span>
                              <span className="text-xs text-[var(--text-secondary)] uppercase font-medium">
                                {material.fileType}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-[var(--text-muted)] text-sm">
                        No materials scheduled for this day
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--highlight-10)' }}>
                  <Calendar className="w-10 h-10 text-[var(--text-secondary)]" />
                </div>
                <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-2">No calendar available</h3>
                <p className="text-[var(--text-secondary)] mb-6">Start your marketing campaign to view the calendar</p>
                <button className="btn btn-primary shadow-md hover:shadow-lg">
                  <Sparkles className="w-5 h-5 mr-2" />
                  Start Campaign
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
